// src/gestion-logistics/hooks/useShipments.ts
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { message } from "antd";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import {
  Shipment,
  ShipmentFilters,
  ShipmentFormData,
} from "../types/shipment.types";

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

interface UseShipmentsResult {
  shipments: Shipment[];
  loading: boolean;
  pagination: PaginationData;
  fetchShipments: (filters?: ShipmentFilters, page?: number) => Promise<void>;
  createShipment: (data: ShipmentFormData) => Promise<Shipment | null>;
  updateShipment: (
    id: number,
    data: Partial<ShipmentFormData>
  ) => Promise<boolean>;
  deleteShipment: (id: number) => Promise<boolean>;
  getShipmentById: (id: number) => Promise<Shipment | null>;
}

export const useShipments = (): UseShipmentsResult => {
  const { selectedCompany } = useSelection();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });

  // Stocker les derniers filtres et page pour le refresh automatique (useRef pour éviter les re-renders)
  const lastFilters = useRef<ShipmentFilters>({});
  const lastPage = useRef<number>(1);

  const fetchShipments = useCallback(
    async (filters?: ShipmentFilters, page?: number) => {
      // Utiliser les derniers filtres/page si non fournis
      const currentFilters = filters !== undefined ? filters : lastFilters.current;
      const currentPage = page !== undefined ? page : lastPage.current;

      // Sauvegarder pour le prochain refresh
      lastFilters.current = currentFilters;
      lastPage.current = currentPage;

      setLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: pagination.limit,
          company_id: selectedCompany, // Envoyer le company_id sélectionné
          ...currentFilters,
        };

        const response = await axios.get(
          `${API_CONFIG.API_URL}/logistics/shipments`,
          { params }
        );

        if (response.data.success) {
          setShipments(response.data.data || []);
          setPagination(response.data.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            total_pages: 0,
          });
        }
      } catch (error: any) {
        console.error("[LOGISTICS] Error fetching shipments:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des dossiers"
        );
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, selectedCompany]
  );

  const getShipmentById = useCallback(
    async (id: number): Promise<Shipment | null> => {
      try {
        const response = await axios.get(
          `${API_CONFIG.API_URL}/logistics/shipments/${id}`
        );

        if (response.data.success) {
          return response.data.data;
        }
        return null;
      } catch (error: any) {
        console.error("[LOGISTICS] Error fetching shipment:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement du dossier"
        );
        return null;
      }
    },
    []
  );

  const createShipment = useCallback(
    async (data: ShipmentFormData): Promise<Shipment | null> => {
      try {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/logistics/shipments`,
          {
            ...data,
            company_id: selectedCompany, // Envoyer le company_id sélectionné
          }
        );

        if (response.data.success) {
          message.success("Dossier créé avec succès");
          await fetchShipments();
          return response.data.data;
        }
        return null;
      } catch (error: any) {
        console.error("[LOGISTICS] Error creating shipment:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la création du dossier"
        );
        return null;
      }
    },
    [fetchShipments, selectedCompany]
  );

  const updateShipment = useCallback(
    async (id: number, data: Partial<ShipmentFormData>): Promise<boolean> => {
      try {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/logistics/shipments/${id}`,
          {
            ...data,
            company_id: selectedCompany, // Envoyer le company_id sélectionné
          }
        );

        if (response.data.success) {
          message.success("Dossier mis à jour avec succès");
          await fetchShipments();
          return true;
        }
        return false;
      } catch (error: any) {
        console.error("[LOGISTICS] Error updating shipment:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la mise à jour du dossier"
        );
        return false;
      }
    },
    [fetchShipments, selectedCompany]
  );

  const deleteShipment = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        const response = await axios.delete(
          `${API_CONFIG.API_URL}/logistics/shipments/${id}`,
          {
            params: { company_id: selectedCompany }, // Envoyer le company_id sélectionné
          }
        );

        if (response.data.success) {
          message.success("Dossier supprimé avec succès");
          await fetchShipments();
          return true;
        }
        return false;
      } catch (error: any) {
        console.error("[LOGISTICS] Error deleting shipment:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la suppression du dossier"
        );
        return false;
      }
    },
    [fetchShipments, selectedCompany]
  );

  // Recharger les dossiers automatiquement quand selectedCompany change
  useEffect(() => {
    if (selectedCompany) {
      fetchShipments();
    } else {
      setShipments([]);
    }
  }, [selectedCompany, fetchShipments]);

  return {
    shipments,
    loading,
    pagination,
    fetchShipments,
    createShipment,
    updateShipment,
    deleteShipment,
    getShipmentById,
  };
};
